import { v4 as uuid } from 'uuid';
import { toastConfig } from 'config';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useRef, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { openDrawer } from 'store/features/menu/menuSlice';
import { propertyArrayList, conditionPayload } from '../utils/index';
import { useNodesState, useEdgesState, MarkerType } from '@xyflow/react';
import { useCreateWorkFlowDesignMutation } from 'store/features/process/processApi';

const useCreateWorkFlowHook = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const flowInstance = useRef(null);
	const createdOnce = useRef(false);
	const reactFlowWrapper = useRef(null);

	const [nodes, setNodes, onNodesChange] = useNodesState([]);
	const [edges, setEdges, onEdgesChange] = useEdgesState([]);

	const [showModal, setShowModal] = useState(false);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [selectedNode, setSelectedNode] = useState(null);
	const [selectedEdge, setSelectedEdge] = useState(null);
	const [isEdgeModalOpen, setIsEdgeModalOpen] = useState(false);

	const { creatorOrganizationId } = useSelector((state) => state.profile.profileData);
	const companyCode = creatorOrganizationId;

	const [createWorkFlowDesign, { isLoading, isSuccess, isError, error }] = useCreateWorkFlowDesignMutation();

	const onDrop = (event) => {
		event.preventDefault();

		const type = event.dataTransfer.getData('application/reactflow');
		if (!type) return;

		const bounds = reactFlowWrapper.current.getBoundingClientRect();

		const position = flowInstance.current.screenToFlowPosition({
			x: event.clientX - bounds.left,
			y: event.clientY - bounds.top,
		});

		const newNode = {
			id: uuid(),
			type,
			position,
			data: {
				label: `${type}`,
				mode: 'both',
				onChangeMode: (newMode) => {
					setNodes((nds) => nds.map((n) => (n.id === newNode.id ? { ...n, data: { ...n.data, setNodes, mode: newMode } } : n)));
				},
			},
		};

		setNodes((nds) => nds.concat(newNode));
	};

	const onDragOver = (event) => {
		event.preventDefault();
		event.dataTransfer.dropEffect = 'move';
	};

	const onConnect = (params) => {
		const updateNodeList = nodes.map((node) => {
			if (node.id === params.source) {
				return {
					...node,
					data: {
						...node.data,
						hasChild: true,
					},
				};
			} else {
				return node;
			}
		});

		setNodes(updateNodeList);

		setEdges((eds) => [
			...eds,
			{
				...params,
				type: 'smoothstep',
				style: { strokeWidth: 2 },
				id: `${params.source}-${params.target}`,
				markerEnd: { type: MarkerType.ArrowClosed },
			},
		]);
	};

	const handleSubmit = () => {
		const designPayload = {
			nodes,
			edges,
		};

		const solution = nodes?.find((item) => item?.type === 'solution')?.data?.solution ?? null;

		const solutionTaskList = nodes?.filter((item) => item?.type === 'solution_task')?.map((item) => item?.data?.solutionTask);

		const convertJson = JSON.stringify(designPayload);
		const convertBase64 = btoa(convertJson);

		const payload = {
			solution,
			companyCode,
			solutionTaskList,
			designBase64: convertBase64,
			propertyArrayList: propertyArrayList({ taskList: solutionTaskList }),
			conditionMaster: conditionPayload({
				conditionNodeList: nodes?.filter((item) => item?.type === 'condition'),
				conditionMaster: {
					status: 'W-I-P',
					conditionName: solution?.name,
					solutionName: solution?.name,
					startTaskActionTaken: solutionTaskList?.[0]?.taskActionTaken,
				},
			}),
		};

		createWorkFlowDesign(payload);
	};

	const handleCreateSolution = () => {
		setIsModalOpen(true);
		const solution = nodes?.find((item) => item?.type === 'solution') ?? null;

		setSelectedNode(solution);
	};

	useEffect(() => {
		dispatch(openDrawer({ drawerOpen: false }));
	}, []);

	useEffect(() => {
		if (isSuccess) {
			toast.success('Success! Action completed.', toastConfig);

			navigate(`/comptroller/work-flow`);
		} else if (isError) {
			toast.error(error?.data?.message, toastConfig);
		}
	}, [isSuccess, isError]);

	useEffect(() => {
		if (createdOnce.current) return;

		createdOnce.current = true;

		const type = 'solution';

		if (!type) return;

		const position = {
			x: 0,
			y: 0,
		};

		const solutionNode = {
			id: uuid(),
			type,
			position,
			data: {
				label: `${type}`,
				mode: 'both',
			},
		};

		const newNode = [
			solutionNode,
			{
				id: uuid(),
				type: 'solution_task',
				position,
				data: {
					label: 'solution_task',
					mode: 'both',
				},
			},
		];

		setNodes((nds) => nds.concat(...newNode));

		setIsModalOpen(true);
		setSelectedNode(solutionNode);
	}, []);

	return {
		nodes,
		edges,
		onDrop,
		setEdges,
		setNodes,
		isLoading,
		showModal,
		onConnect,
		onDragOver,
		isModalOpen,
		setShowModal,
		handleSubmit,
		flowInstance,
		selectedNode,
		selectedEdge,
		onNodesChange,
		onEdgesChange,
		setIsModalOpen,
		setSelectedNode,
		isEdgeModalOpen,
		setSelectedEdge,
		reactFlowWrapper,
		setIsEdgeModalOpen,
		handleCreateSolution,
	};
};

export default useCreateWorkFlowHook;
